import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, Btn, Notice } from "@/dashboard/ui";
import { formatApiError } from "@/lib/AuthContext";
import { CreditCard } from "lucide-react";

const gbp = (p) => `£${(p / 100).toFixed(p % 100 ? 2 : 0)}`;

const STATUS_LABELS = {
  unpaid: "No active subscription",
  active: "Active",
  suspended: "Suspended — payment issue",
  expired: "Cancelled / expired",
};

export default function BillingSettings() {
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");
  const [interval, setInterval] = useState("month");
  const [params, setParams] = useSearchParams();
  const billingMsg = params.get("billing");

  const load = async () => {
    try {
      let { data: s } = await api.get("/subscription");
      // Preview-safe self-heal: if unpaid but a verified Stripe payment exists, reconcile once
      // (server-side, tenant-checked) so status is correct even when webhooks can't reach us.
      if (s.access_status !== "active") {
        try {
          const { data: r } = await api.post("/billing/reconcile");
          if (r.activated) s = (await api.get("/subscription")).data;
        } catch { /* billing not configured — show current state */ }
      }
      const { data: p } = await api.get("/plans");
      setSub(s);
      setPlans(p.plans);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // After Stripe redirects back, confirm the session server-side (never trust the URL alone).
  useEffect(() => {
    const sessionId = params.get("session_id");
    if (billingMsg === "success" && sessionId) {
      api.get(`/billing/status/${sessionId}`)
        .then(() => load())
        .catch(() => {})
        .finally(() => { params.delete("session_id"); setParams(params, { replace: true }); });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, []);

  const subscribe = async (planId) => {
    setBusy(planId);
    try {
      const { data } = await api.post("/billing/checkout", { plan_id: planId, billing_interval: interval, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy("");
    }
  };

  const manage = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy("");
    }
  };

  if (!sub) return null;
  const active = sub.access_status === "active";

  return (
    <Card className="p-6 lg:col-span-2" data-testid="settings-billing-card">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <CreditCard size={16} className="text-[#111111]" />
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Subscription</p>
        </div>
        <span className="rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700">
          Test mode — no real charges
        </span>
      </div>

      {billingMsg === "success" && (
        <div className="mb-4"><Notice testid="billing-success-notice" title="Thank you" body="Your subscription is being confirmed — this page updates as soon as the payment is verified." /></div>
      )}
      {billingMsg === "cancelled" && (
        <div className="mb-4"><Notice testid="billing-cancelled-notice" title="Checkout cancelled" body="No payment was taken. You can subscribe whenever you're ready." /></div>
      )}
      {sub.access_status === "suspended" && (
        <div className="mb-4"><Notice testid="billing-suspended-notice" title="Payment issue" body="Your last payment didn't go through. Please update your payment method to restore AI features." /></div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm font-bold text-[#111111]" data-testid="billing-status">
            {STATUS_LABELS[sub.access_status] || sub.access_status}
            {sub.plan ? ` — ${sub.plan.name}` : ""}
          </p>
          {sub.agreed_price_pence != null && (
            <p className="mt-0.5 text-xs text-neutral-500" data-testid="billing-agreed-price">
              Agreed price: {gbp(sub.agreed_price_pence)}/{sub.billing_interval === "year" ? "year" : "month"} (locked at price version {sub.price_version})
              {sub.renewal_date ? ` · renews ${String(sub.renewal_date).slice(0, 10)}` : ""}
            </p>
          )}
          {!active && (
            <p className="mt-0.5 text-xs text-neutral-500">Subscribe to activate AI voice agents, Talk to AI and AI marketing writing.</p>
          )}
        </div>
        {active && (
          <Btn data-testid="billing-manage-button" disabled={busy === "portal"} onClick={manage}>
            {busy === "portal" ? "Opening…" : "Manage or cancel"}
          </Btn>
        )}
      </div>

      {!active && (
        <>
          {plans.some((p) => (p.interval_options || {}).year) && (
            <div className="mt-5 inline-flex rounded-full border border-neutral-200 p-0.5" data-testid="billing-interval-toggle">
              {[["month", "Monthly"], ["year", "Annual — save 15%"]].map(([v, label]) => (
                <button
                  key={v}
                  data-testid={`billing-interval-${v}`}
                  onClick={() => setInterval(v)}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                    interval === v ? "bg-[#111111] text-white" : "text-neutral-500 hover:text-[#111111]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {plans.map((p) => {
              const opts = p.interval_options || {};
              const yearly = interval === "year" && opts.year;
              const price = yearly ? opts.year.price_pence : p.price_pence;
              return (
                <div key={p.id} className="rounded-2xl border border-neutral-200 p-4" data-testid={`billing-plan-${p.id}`}>
                  <p className="text-sm font-bold text-[#111111]">
                    {p.name} — {gbp(price)}
                    <span className="text-xs font-normal text-neutral-400">/{yearly ? "yr" : "mo"}</span>
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-neutral-500">{p.description}</p>
                  <Btn
                    className="mt-3 w-full"
                    data-testid={`billing-subscribe-${p.id}`}
                    disabled={!!busy}
                    onClick={() => subscribe(p.id)}
                  >
                    {busy === p.id ? "Redirecting…" : `Subscribe — ${gbp(price)}/${yearly ? "yr" : "mo"}`}
                  </Btn>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
}
